import React from 'react';

// components
import {Skeleton} from '@mui/joy';
import {CardBorder} from '../CardBorder';

// styles
import {DesktopWrapper, MobileWrapper} from './CardTable-styles';
import {Subtitle} from '../../styles';

// hooks
import {useTheme} from '@emotion/react';
import {useScreenWidth} from '../../hooks';
import {TableSkeleton} from '../Table';
import CardPaymentSkeleton from '../CardPayment/CardPaymentSkeleton';

type Column =
  | {
      width: string;
      empty: false;
    }
  | {
      empty: true;
    };

type Props = {
  columns: Column[];
  rowCount: number;
};

/**
 * CardTableSkeleton component
 * This component renders a skeleton loader for a table card
 * @param props - The component props
 * @param props.columns - The columns of the table
 * @param props.rowCount - The number of rows to render
 * @return The skeleton table card
 */
const CardTableSkeleton = ({columns, rowCount}: Props) => {
  const {skeleton, screenBreakpoint} = useTheme();
  const screenWidth = useScreenWidth();

  /**
   * Render the card items for mobile view
   * @returns The card items
   */
  const renderCardItems = () => {
    return (
      <>
        {[...Array(rowCount)].map((_, index) => (
          <React.Fragment key={`skeleton_card_table_item_${index}`}>
            <CardPaymentSkeleton />
          </React.Fragment>
        ))}
      </>
    );
  };

  /**
   * Render the table skeleton based on screen width
   * @returns The table skeleton for either mobile or desktop view
   */
  const renderTableSkeleton = () => {
    // Extract integer part from theme breakpoint
    const screenBreakpointValue = screenBreakpoint.md.value;
    if (screenWidth < screenBreakpointValue) {
      return (
        <MobileWrapper>
          <Subtitle>
            <Skeleton variant="text" width={skeleton.text.width.xxl} />
          </Subtitle>
          {renderCardItems()}
        </MobileWrapper>
      );
    }

    return (
      <CardBorder variant="white">
        <DesktopWrapper>
          <Subtitle>
            <Skeleton variant="text" width={skeleton.text.width.xxl} />
          </Subtitle>
          <TableSkeleton columns={columns} rowCount={rowCount} />
        </DesktopWrapper>
      </CardBorder>
    );
  };

  return <>{renderTableSkeleton()}</>;
};

export default CardTableSkeleton;
